/**
 * Opening a model version for labeling — its identity, and its route.
 *
 * ## The bug this exists to end
 *
 * A model reaches the labeling screens under TWO different shapes, and they are
 * not interchangeable:
 *
 * - the **raw Mongo document** the APIs return (`_id`, `name`, `modelType`,
 *   `projectId`) — what `save_model` answers with, and what
 *   `get_one_*_model_info_by_version_id` puts in `response["model"]`;
 * - a **flattened view** some screens build (`model_id`, `model_name`,
 *   `model_type`, `project_id`) — what Video → Images passes.
 *
 * All three labeling screens identified a model as `selectedModel.model_id`
 * ONLY, and on a falsy id they call `resetAll()`. Hand them a raw document and
 * the sequence is:
 *
 *   1. mount, `model_id` undefined  -> resetAll (harmless, nothing loaded yet)
 *   2. the version fetch resolves   -> setClasses(...) + setModelInfo(model)
 *   3. `modelInfo` changed, so the identity effect re-runs -> `model_id` is
 *      STILL undefined -> **resetAll(), wiping what step 2 just loaded**
 *
 * The screen ends up empty with no error anywhere, the network tab showing a
 * perfectly good response. Measured on the live stack: the version held **100
 * data items** and rendered nothing.
 *
 * It read as "the assistant's import did not work". The import was fine — the
 * images were in MinIO and the rows in Mongo. Only the handoff was wrong.
 *
 * So identity is asked for through `modelIdOf`, which accepts either shape.
 */

/**
 * The id of a model, whichever shape it arrived in.
 *
 * Returns `null` rather than `undefined` for "no model", so callers can compare
 * and store it without tripping over the difference.
 */
export const modelIdOf = (model) => {
  if (!model) return null;
  const raw = model.model_id || model._id || null;
  return raw ? String(raw) : null;
};

/** The project a model belongs to, under either shape. */
export const projectIdOf = (model) => {
  if (!model) return null;
  const raw = model.project_id || model.projectId || null;
  return raw ? String(raw) : null;
};

/** The model's task, under either shape, lowercased. */
export const modelTypeOf = (model) => {
  if (!model) return '';
  return String(model.model_type || model.modelType || '').toLowerCase();
};

/**
 * Where a model of this type is labeled.
 *
 * `bounding_box` is the backend's own string for detection; the route is
 * `/DetectionLabel`, which is exactly the kind of mismatch that sends a
 * detection import to the classification studio — where it reads a different
 * Mongo collection, finds nothing, and shows an empty screen that looks like a
 * failed import.
 *
 * Returns `null` for a type with no studio, so a caller can hide the button
 * instead of offering one that lands somewhere wrong.
 */
export const labelingRouteFor = (modelType) => {
  switch (String(modelType || '').toLowerCase()) {
    case 'classification':
      return '/ClassificationLabel';
    case 'bounding_box':
    case 'detection':
      return '/DetectionLabel';
    case 'segmentation':
      return '/SegmentationLabel';
    default:
      return null;
  }
};

/**
 * The `location.state` a labeling screen needs.
 *
 * Carries BOTH spellings deliberately. The screens read a mix — the save path
 * reads `modelInfo?.projectId || modelInfo?.project_id`, the identity check
 * read `model_id` — and normalising to one spelling here would mean auditing
 * every read in three screens to find the ones that break. Sending both is the
 * cheap, reversible half of the fix; `modelIdOf` is the durable half.
 */
export const toLabelingState = (modelDoc, versionId) => {
  if (!modelDoc) return null;
  const id = modelIdOf(modelDoc);
  const projectId = projectIdOf(modelDoc);
  const type = modelTypeOf(modelDoc);
  const ownerId = modelDoc.owner_id || modelDoc.ownerId || null;
  const name = modelDoc.model_name || modelDoc.name || '';
  return {
    model: {
      ...modelDoc,
      model_id: id,
      _id: id,
      model_name: name,
      name,
      model_type: type,
      modelType: type,
      project_id: projectId,
      projectId,
      owner_id: ownerId ? String(ownerId) : null,
      ownerId: ownerId ? String(ownerId) : null,
    },
    versionId: versionId ? String(versionId) : null,
  };
};
